import React, { useCallback, useEffect, useState } from "react";
import { useRefreshAll } from "../hooks/useRefreshAll";
import { Select, Table } from "antd";
import { Link } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { api, type TopFilter, type TopTalker } from "../api";

function fmtBps(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(2) + " G";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}
function fmtPps(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}

interface Props {
  metric: "bps" | "pps";
  dir: "in" | "out";
  color?: string;
  refreshMs?: number;
  geo?: "ixp"; // if set, shows IXP-only top talkers
  /**
   * Optional server-side scope filter. When set, the backend narrows the
   * top-N to IPs inside the given CIDR / ipgroup / zone-node before
   * ranking. Used by the IP Top page tabs (Range / Group / Zone). The
   * Dashboard widget leaves this undefined → ALL traffic.
   */
  filter?: TopFilter;
  /** When set, scope the top-N rollup to a single sFlow source (per-sensor dashboard). */
  sensorId?: number;
}

const LIMIT_OPTIONS = [
  { label: "Top 10", value: 10 },
  { label: "Top 20", value: 20 },
  { label: "Top 50", value: 50 },
  { label: "Top 100", value: 100 },
];

export default function TopTalkersTable({
  metric,
  dir,
  color = "#1677ff",
  refreshMs = 5000,
  geo,
  filter,
  sensorId,
}: Props) {
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState<TopTalker[]>([]);
  // initialLoading is true until the FIRST fetch completes; subsequent
  // 5 s refreshes silently swap the data without flipping a flag, so the
  // antd Table's loading overlay no longer flashes on every poll. The
  // earlier setLoading(true) → setLoading(false) cycle made the widget
  // appear to "blink" continuously which is visually fatiguing on a
  // dashboard the operator stares at all day.
  const [initialLoading, setInitialLoading] = useState(true);

  // Stringify the filter object once for the dep array so the load
  // callback's identity only changes when the filter values themselves
  // change — passing `filter` directly would re-fire useEffect on every
  // parent render even if the contents are unchanged.
  const filterKey = JSON.stringify(filter ?? {});
  const load = useCallback(async () => {
    try {
      const rows = await api.liveTop(metric, dir, limit, geo, filter, sensorId);
      setData(rows);
    } catch {
      /* keep stale on transient error — widget keeps last good frame */
    } finally {
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, dir, limit, geo, filterKey, sensorId]);

  // refreshAllNonce flips when the operator clicks the global "Refresh"
  // button in any chart's toolbar — re-runs the effect so we re-fetch.
  const refreshAllNonce = useRefreshAll();

  useEffect(() => {
    load();
    if (refreshMs > 0) {
      const id = window.setInterval(load, refreshMs);
      return () => window.clearInterval(id);
    }
  }, [load, refreshMs, refreshAllNonce]);

  const fmt = metric === "bps" ? fmtBps : fmtPps;
  const unitLabel = metric === "bps" ? "Bits/s" : "Pkts/s";

  // Compute a lighter variant of the accent color for the gradient end-stop.
  const colorLight = React.useMemo(() => {
    // quick heuristic: parse "#rrggbb" and lighten by mixing with white.
    const m = /^#([0-9a-f]{6})$/i.exec(color);
    if (!m) return color;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    const mix = (c: number) => Math.round(c + (255 - c) * 0.35);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
  }, [color]);

  const columns: ColumnsType<TopTalker> = [
    {
      title: "#",
      dataIndex: "rank",
      width: 40,
      align: "center",
      render: (v: number) => {
        const tier = v === 1 ? "top-1" : v === 2 ? "top-2" : v === 3 ? "top-3" : "";
        return <span className={`tt-rank ${tier}`}>{v}</span>;
      },
    },
    {
      title: "IP Address",
      key: "ip",
      ellipsis: true,
      render: (_: unknown, row: TopTalker) => {
        const label = row.prefix || `node-${row.node_id}`;
        return row.prefix ? (
          <Link
            to={`/ip?q=${encodeURIComponent(row.prefix)}${geo ? `&view=${geo}` : ""}`}
            className="tt-ip-link"
          >
            {label}
          </Link>
        ) : (
          <span className="tt-ip-link">{label}</span>
        );
      },
    },
    {
      // Group — Wansight "IP Group" label. Falls back to the zone name
      // when the matched prefix has no ipgroup_id assigned, so this
      // column is never blank for in-zone IPs even before the operator
      // configures any group rows. Width kept tight (80 px) so the
      // Bits/s value next to it stays visible in narrow dashboard
      // widgets — earlier 110 px clipped Bits/s out of view.
      title: "Group",
      key: "group",
      width: 80,
      ellipsis: true,
      render: (_: unknown, row: TopTalker) => {
        const label = row.group || row.zone || "";
        if (!label) {
          return <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>;
        }
        return (
          <span
            className={`tt-chip${row.group ? "" : " tt-chip-zone"}`}
            title={row.group ? `IP Group: ${row.group}` : `Zone (no IP Group set): ${row.zone}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: unitLabel,
      key: "value",
      width: 90,
      align: "right",
      render: (_: unknown, row: TopTalker) => (
        <span
          style={{
            fontFamily: '"SF Mono", Monaco, Menlo, Consolas, monospace',
            fontSize: 12,
            fontWeight: 600,
            // Inherit the table's text colour so the value matches the
            // dashboard theme automatically. The earlier hardcoded
            // "#0f172a" rendered the digits near-invisible on the dark
            // dashboard skin — same DOM, just unreadable contrast.
            color: "inherit",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmt(metric === "bps" ? row.bps : row.pps)}
        </span>
      ),
    },
    {
      title: (
        <Select
          size="small"
          value={limit}
          onChange={(v) => setLimit(v)}
          options={LIMIT_OPTIONS}
          style={{ width: 82, fontSize: 11 }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      dataIndex: "pct",
      width: 110,
      align: "right",
      render: (v: number) => (
        <div
          className="tt-bar-wrap"
          style={
            {
              ["--tt-color" as string]: color,
              ["--tt-color-light" as string]: colorLight,
              ["--tt-glow" as string]: color + "4d", // ~30% alpha hex suffix
            } as React.CSSProperties
          }
        >
          <div className="tt-bar-track">
            <div className="tt-bar-fill" style={{ width: `${Math.min(v, 100)}%` }} />
          </div>
          <span className="tt-pct">{v.toFixed(0)}%</span>
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={(r) => `${r.rank}-${r.prefix}`}
      size="small"
      loading={initialLoading && data.length === 0}
      dataSource={data}
      columns={columns}
      pagination={false}
      scroll={{ y: "calc(100% - 36px)" }}
      locale={{ emptyText: "No live data" }}
      style={{ height: "100%" }}
    />
  );
}
